import * as React from 'react';

import classNames from 'classnames';
import debounce from 'lodash/debounce';

import {ListViewInfiniteLoading} from 'shells/list-view/list-view-infinite-loading';
import {useResizeObserver} from 'shells/utils/hooks/use-resize-observer';

import type {EventFragmentSparse, SessionUser} from 'nutshell-graphql-types';

import {GET_EVENTS_LIMIT} from './graphql/graphql-wrappers/use-get-timeline-events';
import {getEventsForTimeline} from './helpers';
import {TimelineEmptyState} from './timeline-empty-state';
import {TimelineErrorState} from './timeline-error-state';
import {TimelineEvents} from './timeline-events';
import type {EventForTimeline} from './types';

import styles from './timeline.module.css';

const LOAD_MORE_PIXEL_THRESHOLD = 200;
const SMALL_TIMELINE_THRESHOLD = 425;

type TimelineEntryContextValue = {
    lastEntryId: string | null | undefined;
    isTimelineSmall: boolean | null | undefined;
};

// Context for timeline entries
// @ts-expect-error
export const TimelineEntryContext: React.Context<TimelineEntryContextValue> = React.createContext({
    lastEntryId: undefined,
    isTimelineSmall: undefined,
});

type Props = {
    events: EventFragmentSparse[];
    user: SessionUser | null | undefined;
    entityPageId?: string | null | undefined;
    entityPageName?: string | null | undefined;
    isLoading: boolean;
    isFirstLoad: boolean;
    refetchEvents: () => Promise<any>;
    onFetchMoreEvents: () => Promise<any>;
    hasNextPage: boolean;
    scrollContainerRef?: HTMLDivElement | null | undefined;
    onDelete: (eventId: string) => Promise<any>;
    onSetPinnedStatus?: (arg1: {
        entityToPinId: string;
        pinToEntityId: string;
        status: boolean;
        userName: string;
        changeLogId: string;
    }) => Promise<any>;
    numPinnedEvents?: number;
    isDashboard?: boolean;
    error?: string | null | undefined;
    onRemoveAllFilters?: () => void;
    filterBarHeight?: number | null | undefined;
    // If true, the timeline will use the scrollContainerRef's scroll instead of the window's scroll
    useContainerScroll?: boolean;
};

export function Timeline(props: Props) {
    const {
        events,
        user,
        entityPageId,
        entityPageName,
        isLoading,
        isFirstLoad,
        refetchEvents,
        onFetchMoreEvents,
        hasNextPage,
        scrollContainerRef,
        onDelete: onDeleteEvent,
        onSetPinnedStatus,
        numPinnedEvents,
        isDashboard,
        error,
        onRemoveAllFilters,
        filterBarHeight,
        useContainerScroll,
    } = props;

    const [shouldFetchMore, setShouldFetchMore] = React.useState<boolean>(false);
    const [filteredEventIds, setFilteredEventIds] = React.useState<string[]>([]);
    const timelineContainerRef = React.useRef();
    const canFetchMoreRef = React.useRef<boolean>(false);

    const [width] = useResizeObserver(timelineContainerRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onDelete = React.useCallback(onDeleteEvent, []);

    const timelineEvents: EventForTimeline[] | null | undefined = React.useMemo(() => {
        return getEventsForTimeline(events, isDashboard, user, filteredEventIds);
    }, [events, isDashboard, user, filteredEventIds]);

    const canFetchMore = React.useMemo(() => {
        const currentCanFetchMore = hasNextPage && !isLoading && !shouldFetchMore;
        // Need the ref to pass to the debounce closure to make sure it has the
        // latest value
        canFetchMoreRef.current = currentCanFetchMore;

        return currentCanFetchMore;
    }, [hasNextPage, isLoading, shouldFetchMore]);

    const specialTimelineState = React.useMemo(() => {
        if (error) {
            return <TimelineErrorState />;
        } else if (
            !isLoading &&
            !isFirstLoad &&
            !hasNextPage &&
            timelineEvents &&
            !timelineEvents.length
        ) {
            return <TimelineEmptyState onRemoveAllFilters={onRemoveAllFilters} />;
        } else {
            return null;
        }
    }, [error, isLoading, isFirstLoad, hasNextPage, timelineEvents, onRemoveAllFilters]);

    /**
     * Callback that calculates and returns true if timeline container is scrolled to the bottom
     *
     * @type {(function(): boolean)|*}
     */
    const isScrolledToBottom = React.useCallback((): boolean => {
        // If a scrollContainerRef is passed in, we will use that - otherwise we'll use the timelineContainerRef
        const scrollRef = scrollContainerRef ? scrollContainerRef : timelineContainerRef.current;

        // When using a passed in scrollContainerRef, we will measure against the window
        const useWindow = Boolean(scrollContainerRef && !useContainerScroll);

        if (scrollRef) {
            const boundingClientRect = scrollRef.getBoundingClientRect();
            const scrollContainerHeight = boundingClientRect.height;

            if (useWindow) {
                const windowHeight = window.innerHeight;
                const distanceFromTop = boundingClientRect.top * -1;

                return (
                    scrollContainerHeight - distanceFromTop <=
                    windowHeight + LOAD_MORE_PIXEL_THRESHOLD
                );
            } else {
                const {scrollTop, scrollHeight} = scrollRef;

                return (
                    scrollTop + scrollContainerHeight >= scrollHeight - LOAD_MORE_PIXEL_THRESHOLD
                );
            }
        }

        return false;
    }, [useContainerScroll, scrollContainerRef]);

    /**
     * Effect handles checking for cases where we should just automatically fetch more events without
     * the user needing to trigger the onScroll effect. Including the case where the scroll container
     * is not tall enough for the event to be triggered at all.
     */
    React.useEffect(() => {
        if (
            canFetchMore &&
            ((timelineEvents && timelineEvents.length < GET_EVENTS_LIMIT) || isScrolledToBottom())
        ) {
            setShouldFetchMore(true);
        }
    }, [canFetchMore, isScrolledToBottom, timelineEvents]);

    /**
     * Effect handles fetching more when shouldFetchMore state is triggered
     */
    React.useEffect(() => {
        if (shouldFetchMore) {
            onFetchMoreEvents().then(() => {
                setShouldFetchMore(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFetchMore]);

    /**
     * Effect to handle onScroll listener to fetch more events
     */
    React.useEffect(() => {
        // Define scroll handler, which uses debounce to reduce number of times it runs
        const handleScroll = debounce(() => {
            if (isScrolledToBottom() && canFetchMoreRef.current) {
                setShouldFetchMore(true);
            }
        }, 100);

        // Add event listeners once minimum number of events have been pulled
        if (isDashboard && timelineContainerRef && timelineContainerRef.current) {
            // For the dashboard, the scroll container is the timeline container
            // @ts-expect-error
            timelineContainerRef.current.addEventListener('scroll', handleScroll);
        } else if (useContainerScroll && scrollContainerRef) {
            scrollContainerRef.addEventListener('scroll', handleScroll);
        } else {
            // For other pages, use the app-react-mount container as scroll container
            const appReactMount = document.getElementById('app-react-mount');
            if (appReactMount) {
                appReactMount.addEventListener('scroll', handleScroll);
            }
        }

        // Remove event listeners when this effect returns
        return () => {
            if (isDashboard && timelineContainerRef && timelineContainerRef.current) {
                // @ts-expect-error
                timelineContainerRef.current.removeEventListener('scroll', handleScroll);
            } else {
                const appReactMount = document.getElementById('app-react-mount');
                if (appReactMount) {
                    appReactMount.removeEventListener('scroll', handleScroll);
                }
            }
        };
    }, [isScrolledToBottom, canFetchMore, isDashboard, useContainerScroll, scrollContainerRef]);

    const timelineContainerStyleNames = classNames(
        isDashboard ? styles['timeline-container--dashboard'] : styles['timeline-container']
    );

    return (
        <TimelineEntryContext.Provider
            value={{
                lastEntryId:
                    timelineEvents && timelineEvents.length && !hasNextPage
                        ? // @ts-expect-error
                          timelineEvents[timelineEvents.length - 1].id
                        : undefined,
                isTimelineSmall: Boolean(width && width <= SMALL_TIMELINE_THRESHOLD),
            }}
        >
            <div
                ref={(c) => {
                    // @ts-expect-error
                    timelineContainerRef.current = c;
                }}
                style={filterBarHeight ? {height: `calc(100% - ${filterBarHeight}px)`} : undefined}
                className={timelineContainerStyleNames}
            >
                {specialTimelineState}
                {!isFirstLoad && timelineEvents && user ? (
                    <TimelineEvents
                        timelineEvents={timelineEvents}
                        currentUser={user}
                        entityPageId={entityPageId}
                        entityPageName={entityPageName}
                        onDeleteEvent={onDelete}
                        onSetPinnedStatus={onSetPinnedStatus}
                        onRefetchEvents={refetchEvents}
                        numPinnedEvents={numPinnedEvents}
                        isDashboard={isDashboard}
                        onFilterOutEventId={(eventId) =>
                            setFilteredEventIds((prevIds) => [...prevIds, eventId])
                        }
                    />
                ) : undefined}
                {isFirstLoad || (!isFirstLoad && hasNextPage) ? (
                    <ListViewInfiniteLoading />
                ) : undefined}
            </div>
        </TimelineEntryContext.Provider>
    );
}
